import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { focusZflixNavRail } from './tvSpatialNav';
import { liveSaverOn } from './liveSaver';

/**
 * Live TV: categories | channels | preview.
 *
 * - Channels come from /api/livetv/overview: provider duplicates (A&E, A&E FHD, "USA FHD: A&E") are already merged into
 *   one channel with quality variants, sorted into categories, with what is on now and next.
 * - OK on a channel starts it in the preview pane (the app's player, docked over `data-zflix-livetv-preview-slot`).
 *   OK again on the playing channel, or the Full screen button, expands it. The same player keeps running as a
 *   corner mini player when you leave this screen.
 * - The remote is handled here (three columns), via `window.__zflixOwnNav`, which tvSpatialNav consults first.
 */

const fmtTime = (d) => {
  try {
    return new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch (_) {
    return '';
  }
};

const progressOf = (p, now) => {
  if (!p) return 0;
  const a = new Date(p.start).getTime();
  const b = new Date(p.end).getTime();
  if (!(b > a)) return 0;
  return Math.min(1, Math.max(0, (now - a) / (b - a)));
};

const minsLeft = (p, now) => (p ? Math.max(0, Math.round((new Date(p.end).getTime() - now) / 60000)) : 0);

const chInitials = (name) =>
  String(name || '')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();

/**
 * One channel in the list. Memoised: moving the remote changes which row is selected, and without this every
 * one of the ~400 rows was rebuilt on each key press (slow on a Fire TV).
 */
const ChannelRow = React.memo(function ChannelRow({ ch, selected, playing, starting, minute, onFocusKey, onActivate }) {
  const now = minute * 60000;
  return (
    <button
      type="button"
      className="ltv-ch"
      data-ltv-item
      data-ltv-sel={selected ? 'true' : undefined}
      data-playing={playing ? 'true' : undefined}
      aria-label={`${ch.name}${ch.now ? `, now: ${ch.now.title}` : ''}`}
      onFocus={() => onFocusKey(ch.key)}
      onClick={() => onActivate(ch)}
    >
      <span className="ltv-ch-logo">
        {ch.logo ? (
          <img
            src={ch.logo}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          chInitials(ch.name)
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span className="ltv-ch-name">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ch.name}</span>
          {ch.quality ? <span className="ltv-q">{ch.quality}</span> : null}
          {ch.favorite ? <span className="ltv-star" aria-label="Favorite">★</span> : null}
        </span>
        <span className="ltv-ch-now" style={{ display: 'block' }}>
          {starting ? 'Starting…' : ch.now ? ch.now.title : 'No guide information'}
        </span>
        {ch.now && (selected || playing) ? (
          <span className="ltv-bar" style={{ display: 'block' }}>
            <span style={{ width: `${progressOf(ch.now, now) * 100}%` }} />
          </span>
        ) : null}
      </span>
      <span className="ltv-ch-side">
        {playing ? <span className="ltv-onair">WATCHING</span> : ch.now && selected ? `${minsLeft(ch.now, now)} min left` : ''}
      </span>
    </button>
  );
});

const NO_PROGS = [];

/** "Tonight 6:30 PM" style label for a search result. */
function fmtWhen(iso, now) {
  const d = new Date(iso);
  const days = Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 86400000);
  const day = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${day} ${fmtTime(d)}`;
}

/** Live TV search: everything in the guide, then games the guide does not reach yet. */
function FoundRows({ found, now, recIds, onPick, onLater }) {
  if (!found) return <div className="ltv-empty">Searching the guide…</div>;
  if (!found.programs.length && !found.games.length) return <div className="ltv-empty">Nothing in the guide matches.</div>;
  const isLive = (p) => new Date(p.start).getTime() <= now;
  const row = (p, live) => (
    <button key={p.id} type="button" className="ltv-found" data-ltv-item data-ltv-found onClick={() => onPick(p, live)}>
      <span className={`ltv-found-when${live ? ' is-live' : ''}`}>{live ? 'On now' : fmtWhen(p.start, now)}</span>
      <span className="ltv-found-main">
        <b>{p.title}{recIds.has(p.id) ? ' ●' : ''}</b>
        <small>{[p.channelName, p.episodeTitle, p.overview].filter(Boolean).join(' · ')}</small>
      </span>
    </button>
  );
  const onNow = found.programs.filter(isLive);
  const upcoming = found.programs.filter((p) => !isLive(p));
  return (
    <>
      {onNow.length ? <div className="ltv-found-head">On now · OK to watch</div> : null}
      {onNow.map((p) => row(p, true))}
      {upcoming.length ? <div className="ltv-found-head">Upcoming · OK to record</div> : null}
      {upcoming.map((p) => row(p, false))}
      {found.games.length ? <div className="ltv-found-head">Coming up, not in the guide yet</div> : null}
      {found.games.map((g) => (
        <button key={`${g.start}${g.title}`} type="button" className="ltv-found" data-ltv-item data-ltv-found onClick={() => onLater(g)}>
          <span className="ltv-found-when">{fmtWhen(g.start, now)}</span>
          <span className="ltv-found-main">
            <b>{g.title}{recIds.has(`game-${g.gameId}`) ? ' ●' : ''}</b>
            <small>{g.channelName ? `${g.channelName} · OK to record` : g.network ? `${g.network} · streaming only` : 'Network not announced'}</small>
          </span>
        </button>
      ))}
    </>
  );
}

/** The line above the guide describing the highlighted program. Holds its own state so moving the remote only redraws this. */
function GuideInfo({ api, watching }) {
  const [p, setP] = useState(null);
  api.current = setP;
  return (
    <div className="ltg-info">
      {p ? (
        <>
          <b>{p.title}</b>
          <div>
            {p.channel}
            {p.start ? ` · ${fmtTime(p.start)} – ${fmtTime(p.end)}` : ''}
            {p.episodeTitle ? ` · ${p.episodeTitle}` : ''}
          </div>
          {p.upcoming ? <div>OK to record it or the whole series</div> : null}
        </>
      ) : (
        <div>OK previews the channel, OK again for full screen. Left and Right move through time.</div>
      )}
      {watching ? <div>Watching: {watching} · OK on it again for full screen</div> : null}
    </div>
  );
}

/**
 * Guide: OK on a program that has not started opens this. It owns the remote while open (window.__zflixTopKeys,
 * which tvSpatialNav checks first): Up / Down choose, OK picks, Back closes.
 */
function RecordMenu({ ch, prog, onClose, onDone, onWatch }) {
  const [pick, setPick] = useState(0);
  const [busy, setBusy] = useState(false);
  const pickRef = useRef(0);
  pickRef.current = pick;
  const btns = useRef([]);
  const options = prog.gameId ? [
    { label: 'Record this game', run: () => post('/api/dvr/schedule-game', { gameId: prog.gameId }, `Will record ${prog.title}`) },
    { label: 'Cancel', run: () => onClose() },
  ] : [
    { label: 'Record this episode', run: () => post('/api/dvr/schedule', { programId: prog.id, channelName: ch.name }, `Will record ${prog.title}`) },
    { label: 'Record series (new episodes)', run: () => post('/api/dvr/series', { programId: prog.id, channelName: ch.name, variantIds: (ch.variants || []).map((v) => v.id), newOnly: true }, `Will record new episodes of ${prog.title}`) },
    { label: 'Watch this channel now', run: () => { onClose(); onWatch(ch); } },
    { label: 'Cancel', run: () => onClose() },
  ];
  const optionsRef = useRef(options);
  optionsRef.current = options;
  async function post(url, body, okMsg) {
    if (busy) return;
    setBusy(true);
    let msg = okMsg;
    try {
      const r = await fetch(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) msg = j.error || 'Could not schedule that recording';
      else if (j.warning) msg = j.warning;
    } catch (_) {
      msg = 'Could not schedule that recording';
    }
    setBusy(false);
    onDone(msg);
  }
  useEffect(() => {
    const before = document.activeElement;
    window.__zflixTopKeys = (e) => {
      const k = e.key;
      const code = e.keyCode;
      if (k === 'ArrowUp' || code === 38) setPick((p) => Math.max(0, p - 1));
      else if (k === 'ArrowDown' || code === 40) setPick((p) => Math.min(optionsRef.current.length - 1, p + 1));
      else if (k === 'Enter' || code === 13) optionsRef.current[pickRef.current]?.run();
      else if (k === 'Escape' || k === 'GoBack' || k === 'BrowserBack') onClose();
      return true;
    };
    const prevBack = window.__zflixOwnBack;
    window.__zflixOwnBack = () => {
      onClose();
      return true;
    };
    return () => {
      delete window.__zflixTopKeys;
      if (prevBack) window.__zflixOwnBack = prevBack;
      else delete window.__zflixOwnBack;
      try {
        if (before && document.contains(before)) before.focus({ preventScroll: true });
      } catch (_) {}
    };
  }, [onClose]);
  useEffect(() => {
    try {
      btns.current[pick]?.focus({ preventScroll: true });
    } catch (_) {}
  }, [pick]);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, calc(100vw - 32px))', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{ch.name} · {prog.gameId ? fmtWhen(prog.start, Date.now()) : `${fmtTime(prog.start)} – ${fmtTime(prog.end)}`}</div>
        <div style={{ fontSize: 20, fontWeight: 700, margin: '4px 0 14px' }}>{prog.title}{prog.episodeTitle ? <span style={{ fontWeight: 400, color: 'var(--text2)' }}> · {prog.episodeTitle}</span> : null}</div>
        {options.map((o, i) => (
          <button
            key={o.label}
            ref={(el) => { btns.current[i] = el; }}
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); o.run(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: 8, borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', color: '#fff', border: pick === i ? '2px solid #fff' : '2px solid transparent', background: pick === i ? 'var(--accent)' : 'var(--surface)' }}
          >
            {i < 2 ? '● ' : ''}{o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One channel's row in the guide. Memoised: a key press must not rebuild every program block on screen. */
const GuideRow = React.memo(function GuideRow({ ch, progs, loaded, winStart, winEnd, minute, playing, starting, onProgFocus, onTune, onFuture, recIds }) {
  const now = minute * 60000;
  const span = winEnd - winStart;
  const nowPct = now >= winStart && now < winEnd ? ((now - winStart) / span) * 100 : null;
  return (
    <div className="ltg-row" data-key={ch.key} data-playing={playing ? 'true' : undefined}>
      <div className="ltg-chan">
        <span className="ltv-ch-logo">
          {ch.logo ? (
            <img
              src={ch.logo}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            chInitials(ch.name)
          )}
        </span>
        <span className="ltg-chan-name">{ch.name}</span>
      </div>
      <div className="ltg-track">
        {progs.length === 0 ? (
          <button
            type="button"
            className="ltg-prog is-empty"
            data-ltg-prog
            data-s={winStart}
            data-e={winEnd}
            style={{ left: 0, width: '100%' }}
            onFocus={() => onProgFocus({ title: ch.name, channel: 'No guide information for this channel' })}
            onClick={() => onTune(ch)}
          >
            {starting ? 'Starting…' : loaded ? 'No guide information' : 'Loading…'}
          </button>
        ) : (
          progs.map((p) => {
            const ps = new Date(p.start).getTime();
            const pe = new Date(p.end).getTime();
            const s = Math.max(ps, winStart);
            const e = Math.min(pe, winEnd);
            if (!(e > s)) return null;
            const isNow = ps <= now && pe > now;
            return (
              <button
                key={p.id}
                type="button"
                className={`ltg-prog${isNow ? ' is-now' : ''}${pe <= now ? ' is-past' : ''}`}
                data-ltg-prog
                data-s={s}
                data-e={e}
                style={{ left: `${((s - winStart) / span) * 100}%`, width: `${((e - s) / span) * 100}%` }}
                aria-label={`${p.title}, ${fmtTime(p.start)} to ${fmtTime(p.end)}, ${ch.name}`}
                onFocus={() => onProgFocus({ title: p.title, episodeTitle: p.episodeTitle, start: p.start, end: p.end, channel: ch.name, upcoming: ps > now })}
                onClick={() => (ps > now && onFuture ? onFuture(ch, p) : onTune(ch))}
              >
                <span className="ltg-prog-title">{recIds && recIds.has(p.id) ? <span style={{ color: '#ff4d4d' }}>● </span> : null}{starting && isNow ? 'Starting…' : p.title}</span>
                <span className="ltg-prog-time">
                  {fmtTime(p.start)} – {fmtTime(p.end)}
                </span>
              </button>
            );
          })
        )}
        {nowPct != null ? <div className="ltg-nowline" style={{ left: `${nowPct}%` }} /> : null}
      </div>
    </div>
  );
});

const STYLES = `
.ltv-root { display: flex; flex-direction: column; flex: 1; min-height: 0; width: 100%; }
.ltv-top { display: flex; align-items: center; gap: 12px; padding-bottom: 12px; flex-shrink: 0; }
.ltv-top h2 { font-size: 24px; font-weight: 700; margin: 0; }
.ltv-live { background: var(--accent); color: #fff; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; }
.ltv-status { font-size: 12px; color: var(--text3); }
.ltv-search { margin-left: auto; display: flex; align-items: center; gap: 8px; background: var(--bg3); border: 1px solid var(--border); border-radius: 8px; padding: 7px 12px; }
.ltv-search input { background: none; border: none; outline: none; color: var(--text); font-size: 13px; width: 200px; }
.ltv-search:focus-within { border-color: var(--accent); box-shadow: 0 0 0 2px var(--accent); }
.ltv-cols { display: grid; grid-template-columns: 190px minmax(0, 1fr) 380px; gap: 14px; flex: 1; min-height: 0; }
.ltv-col { min-height: 0; overflow-y: auto; scrollbar-width: thin; padding: 4px; }
.ltv-cat { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 10px 12px; margin-bottom: 4px; border: none; border-radius: 8px; background: transparent; color: var(--text2); font-size: 14px; text-align: left; cursor: pointer; }
.ltv-cat span:last-child { font-size: 11px; color: var(--text3); }
.ltv-cat[aria-current="true"] { background: var(--surface); color: var(--text); font-weight: 600; box-shadow: inset 3px 0 0 var(--accent); }
.ltv-ch { display: grid; grid-template-columns: 44px 52px minmax(0, 1fr) auto; align-items: center; gap: 12px; width: 100%; padding: 8px 12px; margin-bottom: 4px; border: 1px solid transparent; border-radius: 10px; background: var(--surface); color: var(--text); text-align: left; cursor: pointer; }
.ltv-ch-num { font-size: 12px; color: var(--text3); text-align: right; font-variant-numeric: tabular-nums; }
.ltv-ch-logo { width: 52px; height: 36px; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.06); border-radius: 6px; overflow: hidden; font-size: 11px; font-weight: 700; color: var(--text3); }
.ltv-ch-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.ltv-ch-name { font-size: 14px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 6px; }
.ltv-q { font-size: 9px; font-weight: 700; color: var(--text3); border: 1px solid var(--border); border-radius: 3px; padding: 0 4px; flex-shrink: 0; }
.ltv-star { color: #f5c518; font-size: 12px; flex-shrink: 0; }
.ltv-ch-now { font-size: 12px; color: var(--text2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
.ltv-bar { height: 3px; background: rgba(255,255,255,0.12); border-radius: 2px; margin-top: 5px; overflow: hidden; }
.ltv-bar > span { display: block; height: 100%; background: var(--accent); }
.ltv-ch-side { font-size: 11px; color: var(--text3); white-space: nowrap; text-align: right; }
.ltv-ch[data-playing="true"] { border-color: var(--accent); background: rgba(229,9,20,0.10); }
.ltv-onair { color: var(--accent); font-weight: 700; font-size: 10px; letter-spacing: 0.5px; }
.ltv-cat:focus-visible, .ltv-ch:focus-visible, .ltv-btn:focus-visible { outline: 3px solid var(--accent); outline-offset: 1px; }
/* Quiet rows: logo, name and what is on. The progress bar, time left and quality tag only show on the row you are on (or watching). */
.ltv-ch { background: transparent; grid-template-columns: 52px minmax(0, 1fr) auto; margin-bottom: 2px; height: 60px; box-sizing: border-box; overflow: hidden; }
.ltv-ch-num, .ltv-ch .ltv-q, .ltv-ch .ltv-bar, .ltv-ch .ltv-ch-side { display: none !important; }
.ltv-ch[data-ltv-sel="true"], .ltv-ch:focus-visible { background: var(--surface); }
.ltv-ch[data-ltv-sel="true"] .ltv-bar, .ltv-ch:focus-visible .ltv-bar, .ltv-ch[data-playing="true"] .ltv-bar { display: block !important; }
.ltv-ch[data-ltv-sel="true"] .ltv-ch-side, .ltv-ch:focus-visible .ltv-ch-side, .ltv-ch[data-playing="true"] .ltv-ch-side { display: block !important; }
.ltv-ch[data-ltv-sel="true"] .ltv-q, .ltv-ch:focus-visible .ltv-q { display: inline !important; }
.ltv-cat span:last-child { display: none; }
/* Categories live in a dropdown under the top bar, so the channel list / guide gets the full width. */
.ltv-catwrap { position: relative; display: inline-flex; }
.ltv-catbtn { display: flex; align-items: center; gap: 8px; padding: 7px 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg3); color: var(--text); font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.ltv-catbtn::after { content: ''; border: 4px solid transparent; border-top-color: var(--text2); margin-top: 4px; }
.ltv-catbtn:focus { outline: 3px solid var(--accent); outline-offset: 1px; }
.ltv-toggle button:focus { outline: 3px solid var(--accent); outline-offset: 1px; }
.ltv-catmenu { position: absolute; top: 100%; left: 0; z-index: 30; width: 230px; max-height: 70vh; overflow-y: auto; padding: 6px; margin-top: 6px; background: var(--bg2, #16161d); border: 1px solid var(--border); border-radius: 10px; box-shadow: 0 16px 40px rgba(0,0,0,0.6); }
.ltv-catmenu .ltv-cat:focus { outline: 3px solid var(--accent); outline-offset: -1px; background: var(--surface); color: var(--text); }
.ltv-top h2 { white-space: nowrap; }
.ltv-status { white-space: nowrap; }
@media (max-width: 1100px) { html:not(.zflix-tv-apk-density) .ltv-top { flex-wrap: wrap; gap: 8px; } html:not(.zflix-tv-apk-density) .ltv-status { display: none; } html:not(.zflix-tv-apk-density) .ltv-search { margin-left: 0; flex: 1 1 100%; } html:not(.zflix-tv-apk-density) .ltv-search input { width: 100%; } }
.ltv-cols { grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) !important; }
.ltv-cols.is-guide { grid-template-columns: minmax(0, 1fr) !important; }
html.zflix-tv-apk-density .ltv-cols { grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) !important; gap: 12px; }
html.zflix-tv-apk-density .ltv-cols.is-guide { grid-template-columns: minmax(0, 1fr) !important; }
html.zflix-tv-apk-density .ltv-catbtn { padding: 4px 8px; font-size: 10px; border-radius: 5px; gap: 5px; }
html.zflix-tv-apk-density .ltv-catbtn::after { border-width: 3px; margin-top: 3px; }
html.zflix-tv-apk-density .ltv-catmenu { width: 150px; padding: 4px; border-radius: 6px; }
.ltv-pane { display: flex; flex-direction: column; min-height: 0; }
.ltv-slot { position: relative; width: 100%; aspect-ratio: 16 / 9; background: #000; border-radius: 10px; overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.ltv-slot-idle { text-align: center; color: var(--text3); font-size: 13px; padding: 12px; }
.ltv-slot-idle img { max-width: 40%; max-height: 70px; object-fit: contain; display: block; margin: 0 auto 10px; opacity: 0.9; }
.ltv-pane-title { font-size: 18px; font-weight: 700; margin: 12px 0 2px; }
.ltv-pane-sub { font-size: 13px; color: var(--text2); }
.ltv-actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
.ltv-btn { padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border); background: var(--surface); color: var(--text); font-size: 13px; font-weight: 600; cursor: pointer; }
.ltv-btn-primary { background: var(--accent); border-color: var(--accent); color: #fff; }
.ltv-sched { overflow-y: auto; min-height: 0; flex: 1; scrollbar-width: thin; }
.ltv-sched-row { display: grid; grid-template-columns: 74px minmax(0, 1fr); gap: 10px; padding: 7px 0; border-top: 1px solid var(--border); font-size: 13px; }
.ltv-sched-row time { color: var(--text3); font-variant-numeric: tabular-nums; }
.ltv-sched-row.is-now { color: var(--text); font-weight: 600; }
.ltv-sched-ov { font-size: 12px; color: var(--text3); font-weight: 400; margin-top: 2px; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; }
.ltv-empty { color: var(--text3); font-size: 13px; padding: 16px 8px; }
.ltv-found-head { color: var(--text3); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; padding: 10px 8px 6px; }
.ltv-found { display: flex; gap: 12px; width: 100%; text-align: left; padding: 8px; border: 2px solid transparent; border-radius: 8px; background: none; color: var(--text); font: inherit; cursor: pointer; }
.ltv-found:hover, .ltv-found:focus { background: var(--bg3); border-color: var(--accent); outline: none; }
.ltv-found-when { flex: 0 0 96px; color: var(--text2); font-size: 12px; line-height: 1.35; }
.ltv-found-when.is-live { color: var(--accent); font-weight: 700; }
.ltv-found-main { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ltv-found-main b { font-size: 14px; font-weight: 600; }
.ltv-found-main small { color: var(--text3); font-size: 12px; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.ltv-err { font-size: 13px; color: #f5a623; margin-bottom: 8px; }
.ltv-toggle { display: inline-flex; background: var(--surface); border-radius: 8px; padding: 3px; gap: 2px; }
.ltv-toggle button { border: none; background: transparent; color: var(--text2); font-size: 13px; font-weight: 600; padding: 6px 14px; border-radius: 6px; cursor: pointer; }
.ltv-toggle button[aria-pressed="true"] { background: var(--accent); color: #fff; }
.ltv-toggle button:focus-visible { outline: 3px solid var(--accent); outline-offset: 1px; }
.ltv-cols.is-guide { grid-template-columns: 190px minmax(0, 1fr); }
.ltg { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.ltg-top { display: flex; align-items: center; gap: 14px; flex-shrink: 0; padding-bottom: 8px; }
.ltg-slot { width: 240px; aspect-ratio: 16 / 9; background: #000; flex-shrink: 0; }
html.zflix-tv-apk-density .ltg-top { gap: 9px; padding-bottom: 5px; }
html.zflix-tv-apk-density .ltg-slot { width: 168px; }
@media (max-width: 768px) { .ltg-slot { width: 150px; } }
.ltg-info { flex: 1; min-width: 0; min-height: 44px; padding: 2px 4px 0; }
.ltg-info b { font-size: 15px; }
.ltg-info div { font-size: 12px; color: var(--text2); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ltg-scroll { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; scrollbar-width: thin; padding: 0 4px 4px; }
.ltg-head { position: sticky; top: 0; z-index: 3; display: flex; background: var(--bg); padding: 4px 0; border-bottom: 1px solid var(--border); }
.ltg-head-chan, .ltg-chan { width: 190px; flex-shrink: 0; }
.ltg-head-times { position: relative; flex: 1; height: 18px; }
.ltg-head-times span { position: absolute; top: 0; font-size: 11px; color: var(--text3); font-variant-numeric: tabular-nums; padding-left: 4px; border-left: 1px solid var(--border); height: 100%; white-space: nowrap; }
.ltg-row { display: flex; height: 52px; margin-top: 3px; }
.ltg-chan { display: flex; align-items: center; gap: 8px; padding: 0 8px; background: var(--surface2, var(--surface)); border-radius: 8px 0 0 8px; min-width: 0; }
.ltg-chan .ltv-ch-logo { width: 40px; height: 28px; flex-shrink: 0; }
.ltg-chan-name { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.ltg-row[data-playing="true"] .ltg-chan { box-shadow: inset 3px 0 0 var(--accent); }
.ltg-track { position: relative; flex: 1; min-width: 0; background: rgba(255,255,255,0.03); border-radius: 0 8px 8px 0; overflow: hidden; }
.ltg-prog { position: absolute; top: 0; bottom: 0; border: none; border-left: 2px solid var(--bg); background: var(--surface); color: var(--text); text-align: left; padding: 6px 8px; cursor: pointer; overflow: hidden; min-width: 0; }
.ltg-prog.is-now { background: rgba(229,9,20,0.16); }
.ltg-prog.is-past { opacity: 0.55; }
.ltg-prog.is-empty { color: var(--text3); font-size: 12px; }
.ltg-prog-title { display: block; font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ltg-prog-time { display: block; font-size: 11px; color: var(--text3); margin-top: 2px; white-space: nowrap; overflow: hidden; }
.ltg-prog:focus { outline: none; box-shadow: inset 0 0 0 3px var(--accent); background: rgba(229,9,20,0.30); z-index: 2; }
.ltg-nowline { position: absolute; top: 0; bottom: 0; width: 2px; background: var(--accent); z-index: 1; pointer-events: none; }
html.zflix-tv-apk-density .ltv-toggle { padding: 2px; border-radius: 5px; }
html.zflix-tv-apk-density .ltv-toggle button { font-size: 9px; padding: 3px 8px; border-radius: 4px; }
html.zflix-tv-apk-density .ltv-toggle button:focus-visible { outline-width: 2px; }
html.zflix-tv-apk-density .ltv-cols.is-guide { grid-template-columns: 112px minmax(0, 1fr); }
html.zflix-tv-apk-density .ltg-info { min-height: 28px; padding-bottom: 4px; }
html.zflix-tv-apk-density .ltg-info b { font-size: 10px; }
html.zflix-tv-apk-density .ltg-info div { font-size: 8.5px; }
html.zflix-tv-apk-density .ltg-head-chan, html.zflix-tv-apk-density .ltg-chan { width: 118px; }
html.zflix-tv-apk-density .ltg-head-times { height: 12px; }
html.zflix-tv-apk-density .ltg-head-times span { font-size: 8px; }
html.zflix-tv-apk-density .ltg-row { height: 32px; margin-top: 2px; }
html.zflix-tv-apk-density .ltg-chan { gap: 5px; padding: 0 5px; border-radius: 5px 0 0 5px; }
html.zflix-tv-apk-density .ltg-chan .ltv-ch-logo { width: 26px; height: 18px; }
html.zflix-tv-apk-density .ltg-chan-name { font-size: 8.5px; }
html.zflix-tv-apk-density .ltg-prog { padding: 3px 5px; }
html.zflix-tv-apk-density .ltg-prog-title { font-size: 9px; }
html.zflix-tv-apk-density .ltg-prog-time { font-size: 7.5px; margin-top: 1px; }
html.zflix-tv-apk-density .ltg-prog.is-empty { font-size: 8.5px; }
html.zflix-tv-apk-density .ltg-prog:focus-visible { box-shadow: inset 0 0 0 2px var(--accent); }
@media (max-width: 1100px) { .ltv-cols { grid-template-columns: 150px minmax(0, 1fr) 300px; } .ltv-cols.is-guide { grid-template-columns: 150px minmax(0, 1fr); } .ltg-head-chan, .ltg-chan { width: 150px; } }
@media (max-width: 768px) {
  .ltv-cols { display: flex; flex-direction: column; overflow-y: auto; }
  .ltv-col { overflow: visible; flex-shrink: 0; }
  .ltv-cols.is-guide { overflow: hidden; }
  .ltg-head-chan, .ltg-chan { width: 96px; }
  .ltv-pane { order: -1; }
}
html.zflix-tv-apk-density .ltv-top { gap: 8px; padding-bottom: 6px; }
html.zflix-tv-apk-density .ltv-top h2 { font-size: 15px; }
html.zflix-tv-apk-density .ltv-live { font-size: 7px; padding: 1px 4px; }
html.zflix-tv-apk-density .ltv-status { font-size: 9px; }
html.zflix-tv-apk-density .ltv-search { padding: 3px 8px; }
html.zflix-tv-apk-density .ltv-search input { font-size: 10px; width: 150px; }
html.zflix-tv-apk-density .ltv-cols { grid-template-columns: 112px minmax(0, 1fr) 250px; gap: 8px; }
html.zflix-tv-apk-density .ltv-cat { padding: 5px 8px; font-size: 10px; margin-bottom: 2px; border-radius: 5px; }
html.zflix-tv-apk-density .ltv-cat span:last-child { font-size: 8px; }
html.zflix-tv-apk-density .ltv-ch { grid-template-columns: 34px minmax(0, 1fr) auto; gap: 7px; padding: 4px 7px; margin-bottom: 3px; border-radius: 6px; height: 40px; }
html.zflix-tv-apk-density .ltv-ch-num { font-size: 8px; }
html.zflix-tv-apk-density .ltv-ch-logo { width: 34px; height: 23px; font-size: 7px; border-radius: 4px; }
html.zflix-tv-apk-density .ltv-ch-name { font-size: 10px; gap: 4px; }
html.zflix-tv-apk-density .ltv-q { font-size: 6px; padding: 0 2px; }
html.zflix-tv-apk-density .ltv-star { font-size: 8px; }
html.zflix-tv-apk-density .ltv-ch-now { font-size: 8.5px; margin-top: 1px; }
html.zflix-tv-apk-density .ltv-bar { height: 2px; margin-top: 3px; }
html.zflix-tv-apk-density .ltv-ch-side, html.zflix-tv-apk-density .ltv-onair { font-size: 7.5px; }
html.zflix-tv-apk-density .ltv-slot { border-radius: 6px; }
html.zflix-tv-apk-density .ltv-slot-idle { font-size: 9px; }
html.zflix-tv-apk-density .ltv-pane-title { font-size: 13px; margin: 6px 0 1px; }
html.zflix-tv-apk-density .ltv-pane-sub { font-size: 10px; }
html.zflix-tv-apk-density .ltv-actions { gap: 5px; margin: 6px 0; }
html.zflix-tv-apk-density .ltv-btn { padding: 4px 8px; font-size: 9px; border-radius: 5px; }
html.zflix-tv-apk-density .ltv-sched-row { grid-template-columns: 54px minmax(0, 1fr); gap: 8px; padding: 5px 0; font-size: 10.5px; }
html.zflix-tv-apk-density .ltv-sched-ov { font-size: 9px; }
html.zflix-tv-apk-density .ltv-cat:focus-visible, html.zflix-tv-apk-density .ltv-ch:focus-visible, html.zflix-tv-apk-density .ltv-btn:focus-visible { outline-width: 2px; }
`;

export default function LiveTvView({ onPlay, nowPlaying, playerMini, onExpand, onStop }) {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [cat, setCat] = useState('all');
  /** Category dropdown (under the top bar). Left from the channel list / guide opens it. */
  const [catOpen, setCatOpen] = useState(false);
  const catMenu = useRef({ open: () => {}, close: () => {} });
  /** Arriving from the main search keeps what was typed there. */
  const [query, setQuery] = useState(() => {
    const q = window.__zflixLiveTvQuery || '';
    delete window.__zflixLiveTvQuery;
    return q;
  });
  const [selKey, setSelKey] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [startingKey, setStartingKey] = useState(null);
  const [playErr, setPlayErr] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const rootRef = useRef(null);
  const nowPlayingRef = useRef(nowPlaying);
  nowPlayingRef.current = nowPlaying;
  /** 'list' = categories | channels | preview. 'guide' = categories | time grid. */
  const [view, setView] = useState('list');
  /** Guide window: 3 hours starting at the current half hour, moved in 90-minute steps with Left / Right. */
  const [winOffset, setWinOffset] = useState(0);
  const [grid, setGrid] = useState(null);
  const [focusProg, setFocusProg] = useState(null);
  const gridNav = useRef({ shift: () => false, cursor: 0, pending: null });
  const HALF = 30 * 60 * 1000;
  const GRID_HOURS = 3;
  /** A day of 90-minute steps. The guide reaches up to 6 days ahead, and Next day jumps there without 60 presses of Right. */
  const GRID_DAY = 16;
  const GRID_MAX = GRID_DAY * 6;
  const winStart = Math.floor(now / HALF) * HALF + winOffset * 3 * HALF;
  const winEnd = winStart + GRID_HOURS * 3600 * 1000;
  const winDays = Math.round((new Date(winStart).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / 86400000);
  const winDay = winDays === 0 ? 'Today' : winDays === 1 ? 'Tomorrow' : new Date(winStart).toLocaleDateString([], { weekday: 'long' });
  /** Focus stays on the button, so OK OK OK walks through the week. */
  const nextDay = () => setWinOffset((o) => (o + GRID_DAY > GRID_MAX ? 0 : o + GRID_DAY));

  useEffect(() => {
    if (view !== 'guide') return undefined;
    let cancelled = false;
    const get = () =>
      fetch(`/api/livetv/grid?start=${encodeURIComponent(new Date(winStart).toISOString())}&hours=${GRID_HOURS}`, { credentials: 'include' })
        .then((r) => r.json())
        .then((d) => {
          if (!cancelled && d && d.rows) setGrid(d);
        })
        .catch(() => {});
    get();
    const iv = setInterval(get, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [view, winStart]);

  /** Guide rows are windowed like the channel list: only the rows near the visible part are in the page. */
  const gRef = useRef(null);
  const gStep = useRef(55);
  const gShownRef = useRef([]);
  const [gWin, setGWin] = useState({ start: 0, end: 36 });
  const computeG = useCallback(() => {
    const el = gRef.current;
    if (!el) return;
    const h = gStep.current;
    const first = Math.floor(el.scrollTop / h);
    const visible = Math.ceil((el.clientHeight || 600) / h);
    const start = Math.max(0, Math.floor((first - 8) / 4) * 4);
    const end = start + visible + 20;
    setGWin((w) => (w.start === start && w.end === end ? w : { start, end }));
  }, []);
  useEffect(() => {
    const rows = gRef.current?.querySelectorAll('.ltg-row');
    if (rows && rows.length > 1) {
      const step = rows[1].offsetTop - rows[0].offsetTop;
      if (step > 10 && step !== gStep.current) {
        gStep.current = step;
        computeG();
      }
    }
  });

  /** After the window moves (or the grid first shows), put the remote back on the row it was on. */
  useEffect(() => {
    const pend = gridNav.current.pending;
    if (!grid || !pend || view !== 'guide') return;
    if (new Date(grid.start).getTime() !== winStart) return;
    gridNav.current.pending = null;
    /** The wanted row may be outside the rendered window (e.g. the playing channel far down the list). */
    const have = [...(rootRef.current?.querySelectorAll('.ltg-row') || [])].some((r) => r.getAttribute('data-key') === pend.key);
    const idx = have ? -1 : gShownRef.current.findIndex((c) => c.key === pend.key);
    if (idx > 0 && gRef.current) {
      gRef.current.scrollTop = Math.max(0, idx * gStep.current - gRef.current.clientHeight / 2 + gStep.current);
      computeG();
    }
    const t = setTimeout(() => {
      const root = rootRef.current;
      if (!root) return;
      const rows = [...root.querySelectorAll('.ltg-row')];
      const row = rows.find((r) => r.getAttribute('data-key') === pend.key) || rows[0];
      const progs = row ? [...row.querySelectorAll('[data-ltg-prog]')] : [];
      const at = (t) => progs.find((p) => Number(p.dataset.s) <= t && Number(p.dataset.e) > t);
      const el = (pend.time ? at(pend.time) : null) || (pend.edge === 'last' ? progs[progs.length - 1] : pend.edge === 'now' ? progs.find((p) => p.classList.contains('is-now')) || progs[0] : progs[0]);
      if (el) {
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: 'nearest' });
      }
    }, 110);
    return () => clearTimeout(t);
  }, [grid, winStart, view, computeG]);

  /** A failed load retries by itself after a few seconds; the message only shows when there is no list to show at all. */
  const hasDataRef = useRef(false);
  const retryRef = useRef(null);
  const load = useCallback(() => {
    const failed = (msg) => {
      if (!hasDataRef.current) setLoadErr(`${msg} Trying again…`);
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => load(), 5000);
    };
    fetch('/api/livetv/overview', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d && Array.isArray(d.channels)) {
          hasDataRef.current = true;
          clearTimeout(retryRef.current);
          setData(d);
          setLoadErr('');
        } else failed(typeof d?.error === 'string' ? d.error : 'Could not load channels.');
      })
      .catch(() => failed('Could not load channels.'));
  }, []);
  useEffect(() => () => clearTimeout(retryRef.current), []);

  /** Channel list + "on now" refresh themselves every minute, so the guide never needs a manual reload. */
  useEffect(() => {
    load();
    const a = setInterval(load, 60 * 1000);
    const b = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, [load]);

  /** First load: put the remote on the channel list (the one playing, if any). */
  const didInitialFocus = useRef(false);
  const shownRef = useRef([]);
  const scrollToIndexRef = useRef(null);
  useEffect(() => {
    if (!data || didInitialFocus.current) return;
    didInitialFocus.current = true;
    const t = setTimeout(() => {
      const root = rootRef.current;
      const a = document.activeElement;
      if (!root || (a && a.closest && a.closest('.sidebar'))) return;
      if (a && root.contains(a) && a.closest('[data-ltv-col="channels"]')) return;
      const pk = nowPlayingRef.current?.zflixLiveKey;
      const idx = pk ? shownRef.current.findIndex((c) => c.key === pk) : -1;
      if (idx > 0) scrollToIndexRef.current?.(idx);
      setTimeout(() => {
        const row = root.querySelector('.ltv-ch[data-playing="true"]') || root.querySelector('.ltv-ch');
        if (row) {
          row.focus({ preventScroll: true });
          row.scrollIntoView({ block: 'nearest' });
        }
      }, idx > 0 ? 80 : 0);
    }, 120);
    return () => clearTimeout(t);
  }, [data]);

  const channels = data?.channels || [];
  const favCount = useMemo(() => channels.filter((c) => c.favorite).length, [channels]);
  const recent = useMemo(
    () => channels.filter((c) => c.lastWatchedAt > 0).sort((a, b) => b.lastWatchedAt - a.lastWatchedAt).slice(0, 20),
    [channels],
  );

  const cats = useMemo(() => {
    const list = [];
    if (favCount) list.push({ id: 'favorites', label: 'Favorites', count: favCount });
    if (recent.length) list.push({ id: 'recent', label: 'Recent', count: recent.length });
    list.push({ id: 'all', label: 'All channels', count: channels.length });
    for (const c of data?.categories || []) list.push(c);
    return list;
  }, [data, channels.length, favCount, recent.length]);

  useEffect(() => {
    if (cats.length && !cats.some((c) => c.id === cat)) setCat('all');
  }, [cats, cat]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    /** The 600 ESPN+ channels live in their own category and stay out of All. */
    const everyday = channels.filter((c) => c.category !== 'ESPN+');
    let list = everyday;
    if (cat === 'favorites') list = channels.filter((c) => c.favorite);
    else if (cat === 'recent') list = recent;
    else if (cat !== 'all') list = channels.filter((c) => c.category === cat);
    if (q) list = (cat === 'all' ? everyday : list).filter((c) => c.name.toLowerCase().includes(q) || (c.now?.title || '').toLowerCase().includes(q));
    return list;
  }, [channels, recent, cat, query]);

  /** Search also covers the whole guide, not just channels and what is on now. Results live in the channel column. */
  const searching = query.trim().length >= 2;
  const [found, setFound] = useState(null);
  useEffect(() => {
    setFound(null);
    if (!searching) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`/api/livetv/search?q=${encodeURIComponent(query.trim())}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!cancelled) setFound(d || { programs: [], games: [] });
        })
        .catch(() => {
          if (!cancelled) setFound({ programs: [], games: [] });
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, searching]);
  useEffect(() => {
    if (searching) setView('list');
  }, [searching]);

  const playingKey = nowPlaying?.zflixLiveKey || null;
  const sel = useMemo(
    () => channels.find((c) => c.key === selKey) || channels.find((c) => c.key === playingKey) || shown[0] || null,
    [channels, selKey, playingKey, shown],
  );

  /**
   * Windowed list: only the rows near the visible part of the channel column are in the page (a Fire TV crawls
   * with 400 rows of logos). Rows have a fixed height; spacers above and below keep the scroll bar honest.
   * Phones scroll the whole page instead of the column, so they keep the full list.
   */
  const listRef = useRef(null);
  const rowStep = useRef(62);
  const windowed = typeof window !== 'undefined' && window.innerWidth > 768 && shown.length > 60 && !searching;
  const [win, setWin] = useState({ start: 0, end: 50 });
  const computeWin = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const h = rowStep.current;
    const first = Math.floor(el.scrollTop / h);
    const visible = Math.ceil((el.clientHeight || 600) / h);
    const start = Math.max(0, Math.floor((first - 10) / 5) * 5);
    const end = start + visible + 26;
    setWin((w) => (w.start === start && w.end === end ? w : { start, end }));
  }, []);
  const scrollToIndex = useCallback(
    (i) => {
      const el = listRef.current;
      if (!el || i < 0) return;
      el.scrollTop = Math.max(0, i * rowStep.current - el.clientHeight / 2 + rowStep.current);
      computeWin();
    },
    [computeWin],
  );
  /** Real row pitch (differs between desktop and the TV app's compact size). */
  useEffect(() => {
    const rows = listRef.current?.querySelectorAll('.ltv-ch');
    if (rows && rows.length > 1) {
      const step = rows[1].offsetTop - rows[0].offsetTop;
      if (step > 10 && step !== rowStep.current) {
        rowStep.current = step;
        computeWin();
      }
    }
  });
  /** New category / search / view: back to the top of the list. */
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    computeWin();
  }, [cat, query, view, computeWin]);
  useEffect(() => {
    if (gRef.current) gRef.current.scrollTop = 0;
    computeG();
  }, [cat, query, computeG]);
  const gWindowed = typeof window !== 'undefined' && shown.length > 50;
  const gStart = gWindowed ? Math.min(gWin.start, Math.max(0, shown.length - 1)) : 0;
  const gEnd = gWindowed ? Math.min(shown.length, gWin.end) : shown.length;
  gShownRef.current = shown;
  shownRef.current = shown;
  scrollToIndexRef.current = scrollToIndex;
  const winStart2 = windowed ? Math.min(win.start, Math.max(0, shown.length - 1)) : 0;
  const winEnd2 = windowed ? Math.min(shown.length, win.end) : searching ? Math.min(shown.length, 30) : shown.length;

  /** Schedule for the highlighted channel (short delay so moving through the list does not fire a request per row). */
  useEffect(() => {
    if (!sel) {
      setSchedule([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`/api/livetv/schedule/${encodeURIComponent(sel.guideChannelId)}`, { credentials: 'include' })
        .then((r) => r.json())
        .then((list) => {
          if (!cancelled) setSchedule(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setSchedule([]);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [sel?.key, sel?.guideChannelId]);

  /**
   * Starting a channel never blocks the next one. A channel that is off the air can take the server a long time to
   * give up on; before, OK on any other channel did nothing until it had. Now a new OK abandons the earlier attempt,
   * and each attempt is given 20 seconds.
   */
  const startSeq = useRef(0);
  const startAbort = useRef(null);
  const start = useCallback(
    async (ch, opts = {}) => {
      if (!ch) return;
      const seq = (startSeq.current += 1);
      try {
        startAbort.current?.abort();
      } catch (_) {}
      const stale = () => seq !== startSeq.current;
      setStartingKey(ch.key);
      setPlayErr('');
      try {
        const cur = nowPlayingRef.current;
        if (cur?.liveStreamId || cur?.playSessionId) {
          await fetch('/api/livetv/stop', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ liveStreamId: cur.liveStreamId, playSessionId: cur.playSessionId }),
          }).catch(() => {});
          await new Promise((r) => setTimeout(r, 500));
        }
        /** Best quality first; if that copy of the channel will not start, fall back to the next one. */
        let lastMsg = 'Could not start channel.';
        for (const v of ch.variants) {
          if (stale()) return;
          const ac = new AbortController();
          startAbort.current = ac;
          const timer = setTimeout(() => ac.abort(), 20000);
          try {
            const r = await fetch(`/api/livetv/${encodeURIComponent(v.id)}/play`, { credentials: 'include', signal: ac.signal });
            const d = await r.json().catch(() => ({}));
            clearTimeout(timer);
            if (stale()) {
              /** Another channel was chosen meanwhile: release the stream this attempt opened. */
              if (d && d.liveStreamId) {
                fetch('/api/livetv/stop', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ liveStreamId: d.liveStreamId, playSessionId: d.playSessionId }),
                }).catch(() => {});
              }
              return;
            }
            if (r.ok && d.streamUrl) {
              /** Data saver: a smaller copy made at home. If it cannot start, the normal one still plays. */
              let streamUrl = d.streamUrl;
              if (liveSaverOn() && /\/api\/livetv\/direct\/[^/]+\/index\.m3u8$/.test(streamUrl)) {
                try {
                  const low = await fetch(streamUrl.replace(/index\.m3u8$/, 'low'), { method: 'POST', credentials: 'include' });
                  const j = await low.json();
                  if (low.ok && j.streamUrl) streamUrl = j.streamUrl;
                } catch (_) {}
                if (stale()) return;
              }
              onPlay(
                {
                  id: v.id,
                  name: ch.name,
                  title: ch.name,
                  logo: ch.logo,
                  streamUrl,
                  liveStreamId: d.liveStreamId,
                  playSessionId: d.playSessionId,
                  zflixLiveKey: ch.key,
                },
                { mini: opts.mini !== false },
              );
              setData((prev) =>
                prev ? { ...prev, channels: prev.channels.map((c) => (c.key === ch.key ? { ...c, lastWatchedAt: Date.now() } : c)) } : prev,
              );
              return;
            }
            if (typeof d.error === 'string' && d.error.length < 120) lastMsg = d.error;
          } catch (_) {
            clearTimeout(timer);
            if (stale()) return;
            lastMsg = `${ch.name} is not responding right now. Try another channel.`;
          }
        }
        if (!stale()) setPlayErr(lastMsg);
      } finally {
        if (!stale()) setStartingKey(null);
      }
    },
    [onPlay],
  );

  /**
   * Full-screen player: Up / Down on the remote flip through this channel list (the current category / filter),
   * wrapping at the ends (Zach, 2026-10-29). The player asks for the neighbour, then tunes it after a short pause.
   */
  const startRef = useRef(start);
  startRef.current = start;
  const channelsRef = useRef([]);
  channelsRef.current = data?.channels || [];
  /** The Watch button on the Sports page: start the channel it names, with that copy first. */
  useEffect(() => {
    const id = window.__zflixLiveTvPlay;
    if (!id || !channelsRef.current.length) return;
    delete window.__zflixLiveTvPlay;
    const ch = channelsRef.current.find((c) => (c.variants || []).some((v) => v.id === id));
    if (!ch) return;
    setSelKey(ch.key);
    startRef.current({ ...ch, variants: [...ch.variants.filter((v) => v.id === id), ...ch.variants.filter((v) => v.id !== id)] }, { mini: false });
  });
  useEffect(() => {
    /** The player gave up on this copy: the next one, going round. False when there is no other copy. */
    window.__zflixLiveTvNextCopy = (key, variantId) => {
      const ch = channelsRef.current.find((c) => c.key === key);
      const all = ch?.variants || [];
      if (all.length < 2) return false;
      const i = all.findIndex((v) => v.id === variantId);
      startRef.current({ ...ch, variants: [...all.slice(i + 1), ...all.slice(0, i + 1)] }, { mini: false });
      return true;
    };
    window.__zflixLiveTvNeighbor = (fromKey, step) => {
      const list = shownRef.current || [];
      if (!list.length) return null;
      const i = list.findIndex((c) => c.key === fromKey);
      const j = i < 0 ? (step > 0 ? 0 : list.length - 1) : (((i + step) % list.length) + list.length) % list.length;
      const n = list[j];
      return n ? { key: n.key, name: n.name, logo: n.logo, program: n.now?.title || '' } : null;
    };
    window.__zflixLiveTvList = () => (shownRef.current || []).map((c) => ({ key: c.key, name: c.name, logo: c.logo, now: c.now?.title || '' }));
    window.__zflixLiveTvTune = (key) => {
      const ch = (shownRef.current || []).find((c) => c.key === key);
      if (!ch) return false;
      setSelKey(ch.key);
      startRef.current(ch, { mini: false });
      return true;
    };
    return () => {
      delete window.__zflixLiveTvNextCopy;
      delete window.__zflixLiveTvNeighbor;
      delete window.__zflixLiveTvList;
      delete window.__zflixLiveTvTune;
    };
  }, []);

  const activateRef = useRef(null);
  const activateStable = useCallback((ch) => activateRef.current?.(ch), []);
  const activate = (ch) => {
    setSelKey(ch.key);
    if (playingKey === ch.key) onExpand?.();
    else start(ch);
  };

  activateRef.current = activate;

  /** Guide: OK on any program plays that channel in the small preview above the grid; OK again goes full screen. */
  const tune = (ch) => {
    if (playingKey === ch.key) onExpand?.();
    else start(ch);
  };

  /** Guide rows are memoised, so they get stable callbacks; the info line above the grid updates without re-rendering the grid. */
  const tuneRef = useRef(null);
  tuneRef.current = tune;
  const tuneStable = useCallback((ch) => tuneRef.current?.(ch), []);
  const infoApi = useRef(null);
  const onProgFocus = useCallback((info) => infoApi.current?.(info), []);
  /** DVR: which guide programs are set to record (red dot), and the record menu for a program that has not started. */
  const [recAsk, setRecAsk] = useState(null);
  const [recIds, setRecIds] = useState(() => new Set());
  const [recNote, setRecNote] = useState('');
  const loadRecIds = useCallback(() => {
    fetch('/api/dvr', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const ids = new Set();
        for (const x of d.scheduled || []) if (x.programId) ids.add(x.programId);
        for (const x of d.recordings || []) if (x.programId && x.status === 'recording') ids.add(x.programId);
        setRecIds(ids);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    loadRecIds();
    const iv = setInterval(loadRecIds, 60000);
    return () => clearInterval(iv);
  }, [loadRecIds]);
  useEffect(() => {
    if (!recNote) return undefined;
    const t = setTimeout(() => setRecNote(''), 4000);
    return () => clearTimeout(t);
  }, [recNote]);
  const futureStable = useCallback((ch, p) => setRecAsk({ ch, p }), []);
  const closeRecAsk = useCallback(() => setRecAsk(null), []);
  /** Search result: on now tunes the channel, anything later opens the record menu. */
  const pickFound = (p, live) => {
    const ch = channels.find((c) => (c.variants || []).some((v) => p.channelIds.includes(v.id)));
    if (!ch) {
      setRecNote(`${p.channelName || 'That channel'} is not in your channel list`);
      return;
    }
    setSelKey(ch.key);
    if (live) tune(ch);
    else setRecAsk({ ch, p: { id: p.id, title: p.title, start: new Date(p.start), end: new Date(p.end) } });
  };
  const noteLater = (g) => {
    if (g.channelName) setRecAsk({ ch: { name: g.channelName }, p: { id: `game-${g.gameId}`, title: g.title, gameId: g.gameId, start: g.start } });
    else setRecNote(g.network ? `${g.title} is only streaming on ${g.network}, so Zflix cannot record it` : `${g.title} has no channel announced yet`);
  };
  const gridLoaded = !!grid && new Date(grid.start).getTime() === winStart;

  const switchView = (v) => {
    setView(v);
    if (v === 'guide') {
      setWinOffset(0);
      gridNav.current.pending = { key: playingKey || sel?.key || null, edge: 'now' };
    }
  };

  gridNav.current.shift = (dir, key, time) => {
    if (dir < 0 && winOffset <= 0) return false;
    gridNav.current.pending = { key, time, edge: dir > 0 ? 'first' : 'last' };
    setWinOffset((o) => Math.min(GRID_MAX, Math.max(0, o + dir)));
    return true;
  };

  const toggleFav = (ch) => {
    const favorite = !ch.favorite;
    setData((prev) => (prev ? { ...prev, channels: prev.channels.map((c) => (c.key === ch.key ? { ...c, favorite } : c)) } : prev));
    fetch('/api/livetv/favorite', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: ch.key, favorite }),
    }).catch(() => {});
  };

  const fromAppHeader = useRef(false);
  /** Remote: Left/Right change column, Up/Down move inside it. Returning false hands the key back to the app. */
  useEffect(() => {
    const colOf = (el) => (el && el.closest ? el.closest('[data-ltv-col]')?.getAttribute('data-ltv-col') : null);
    const items = (col) => [...(rootRef.current?.querySelectorAll(`[data-ltv-col="${col}"] [data-ltv-item]`) || [])];
    const go = (el) => {
      if (!el) return false;
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return true;
    };
    const preferred = (col) => {
      const list = items(col);
      return list.find((el) => el.hasAttribute('data-ltv-found')) || list.find((el) => el.getAttribute('aria-current') === 'true' || el.getAttribute('data-ltv-sel') === 'true') || list[0];
    };
    /** Guide grid helpers: rows of program blocks, each block carrying its start / end time. */
    const gridRows = () => [...(rootRef.current?.querySelectorAll('.ltg-row') || [])];
    const progsOf = (row) => [...(row?.querySelectorAll('[data-ltg-prog]') || [])];
    const atTime = (row, t) => {
      const list = progsOf(row);
      return list.find((p) => Number(p.dataset.s) <= t && Number(p.dataset.e) > t) || list.find((p) => Number(p.dataset.s) > t) || list[list.length - 1];
    };
    const gridEntry = () => {
      const rows = gridRows();
      const row = rows.find((r) => r.getAttribute('data-playing') === 'true') || rows[0];
      return atTime(row, Date.now());
    };
    const topItems = () => [...(rootRef.current?.querySelectorAll('[data-ltv-top]') || [])];
    const topCurrent = () => topItems().find((el) => el.getAttribute('aria-pressed') === 'true') || topItems()[0];
    const content = () => go(preferred('channels')) || go(gridEntry()) || go(topItems()[0]);

    window.__zflixOwnNav = (key, active) => {
      const root = rootRef.current;
      /** Down from the app's top bar stops at this screen's search box instead of jumping back into the list. */
      if (key === 'ArrowDown' && root && active?.closest?.('[data-zflix-app-header="true"]')) {
        const box = root.querySelector('.ltv-search input');
        if (box) {
          fromAppHeader.current = true;
          box.focus({ preventScroll: true });
          fromAppHeader.current = false;
          return true;
        }
      }
      if (!root || !active || !root.contains(active)) return false;

      /** Top strip: Channels / Guide switch and the filter box. */
      if (active.hasAttribute('data-ltv-top')) {
        const tops = topItems();
        const ti = tops.indexOf(active);
        const typing = active.tagName === 'INPUT' && active.value;
        if (key === 'ArrowDown') return content();
        if (key === 'ArrowLeft' && !typing) return ti > 0 ? go(tops[ti - 1]) : true;
        if (key === 'ArrowRight' && !typing) return ti < tops.length - 1 ? go(tops[ti + 1]) : true;
        return false;
      }

      /** Time grid. */
      if (active.hasAttribute('data-ltg-prog')) {
        const row = active.closest('.ltg-row');
        const rows = gridRows();
        const ri = rows.indexOf(row);
        const list = progsOf(row);
        const pi = list.indexOf(active);
        const nav = gridNav.current;
        if (key === 'ArrowRight') {
          if (pi < list.length - 1) {
            nav.cursor = Number(list[pi + 1].dataset.s) + 60000;
            return go(list[pi + 1]);
          }
          nav.cursor = Number(active.dataset.e) + 60000;
          nav.shift(1, row.getAttribute('data-key'), nav.cursor);
          return true;
        }
        if (key === 'ArrowLeft') {
          if (pi > 0) {
            nav.cursor = Number(list[pi - 1].dataset.s) + 60000;
            return go(list[pi - 1]);
          }
          if (nav.shift(-1, row.getAttribute('data-key'), Number(active.dataset.s) - 60000)) {
            nav.cursor = Number(active.dataset.s) - 60000;
            return true;
          }
          catMenu.current.open();
          return true;
        }
        const t = Math.max(nav.cursor || 0, Number(active.dataset.s) + 60000);
        const clamp = Math.min(t, Number(active.dataset.e) - 1000);
        if (key === 'ArrowDown') return ri < rows.length - 1 ? go(atTime(rows[ri + 1], clamp)) : true;
        if (key === 'ArrowUp') {
          if (ri > 0) return go(atTime(rows[ri - 1], clamp));
          /** Windowed rows still catching up with a held key: not really the top row. */
          if ((active.closest('.ltg-scroll')?.scrollTop || 0) > 80) return true;
          return go(topCurrent());
        }
        return false;
      }

      const col = colOf(active);
      if (!col) return false;
      const list = items(col);
      const i = list.indexOf(active);
      /** Open category menu: Up/Down inside it, OK picks, Right closes back to the channels, Left closes to the side menu. */
      if (col === 'cats') {
        if (key === 'ArrowDown') return i < list.length - 1 ? go(list[i + 1]) : true;
        if (key === 'ArrowUp') return i > 0 ? go(list[i - 1]) : true;
        catMenu.current.close();
        if (key === 'ArrowLeft') focusZflixNavRail();
        else content();
        return true;
      }
      if (key === 'ArrowDown') return i < list.length - 1 ? go(list[i + 1]) : true;
      if (key === 'ArrowUp') {
        if (i > 0) return go(list[i - 1]);
        /** Windowed list still catching up with a held key: this is not really the top row, so wait. */
        if (col === 'channels' && (active.closest('[data-ltv-col]')?.scrollTop || 0) > 80) return true;
        /** Top of a column: up to the Channels / Guide switch, and from there the app takes over (top search). */
        return go(topCurrent());
      }
      if (key === 'ArrowLeft') {
        if (col === 'pane') return go(preferred('channels'));
        if (col === 'channels') {
          catMenu.current.open();
          return true;
        }
        focusZflixNavRail();
        return true;
      }
      if (key === 'ArrowRight') {
        if (col === 'cats') return go(preferred('channels')) || go(gridEntry()) || true;
        if (col === 'channels') return go(preferred('pane')) || true;
        return true;
      }
      return false;
    };
    return () => {
      delete window.__zflixOwnNav;
    };
  }, []);

  const focusContent = () => {
    const el = rootRef.current?.querySelector('[data-ltv-col="channels"] [data-ltv-sel="true"], [data-ltv-col="channels"] [data-ltv-item], .ltg-prog.is-now, [data-ltg-prog]');
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest' });
    }
  };
  catMenu.current.open = () => setCatOpen(true);
  catMenu.current.close = () => setCatOpen(false);
  const pickCat = (id) => {
    setCat(id);
    setCatOpen(false);
    setTimeout(focusContent, 120);
  };
  /** Arriving from the side menu must land on a channel, not on the top bar (its first button is the category picker). */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onIn = (e) => {
      if (!e.target?.hasAttribute?.('data-ltv-top')) return;
      if (fromAppHeader.current) return;
      if (e.relatedTarget && root.contains(e.relatedTarget)) return;
      if (root.querySelector('[data-ltv-col="channels"] [data-ltv-item], [data-ltg-prog]')) focusContent();
    };
    root.addEventListener('focusin', onIn);
    return () => root.removeEventListener('focusin', onIn);
  }, []);

  /** Menu just opened: put the remote on the current category. Back closes it instead of leaving Live TV. */
  useEffect(() => {
    if (!catOpen) return undefined;
    const t = setTimeout(() => {
      const menu = rootRef.current?.querySelector('.ltv-catmenu');
      const el = menu?.querySelector('[aria-current="true"]') || menu?.querySelector('[data-ltv-item]');
      if (el) {
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: 'nearest' });
      }
    }, 40);
    window.__zflixOwnBack = () => {
      setCatOpen(false);
      setTimeout(focusContent, 60);
      return true;
    };
    return () => {
      clearTimeout(t);
      delete window.__zflixOwnBack;
    };
  }, [catOpen]);

  const selIsPlaying = !!sel && playingKey === sel.key;
  const initials = (name) =>
    String(name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 3)
      .toUpperCase();
  const guideWeak = data && data.total > 0 && data.withGuide / data.total < 0.5;

  return (
    <div className="ltv-root" ref={rootRef} data-zflix-livetv-root="true" data-zflix-own-nav="true">
      <style>{STYLES}</style>
      {recAsk ? (
        <RecordMenu
          ch={recAsk.ch}
          prog={recAsk.p}
          onClose={closeRecAsk}
          onWatch={(c) => tuneStable(c)}
          onDone={(msg) => {
            setRecAsk(null);
            setRecNote(msg);
            loadRecIds();
          }}
        />
      ) : null}
      {recNote ? (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', zIndex: 9001, padding: '12px 18px', borderRadius: 10, background: 'rgba(10,10,14,0.92)', border: '1px solid var(--border)', color: '#fff', fontSize: 15, pointerEvents: 'none' }}>{recNote}</div>
      ) : null}
      <div className="ltv-top">
        <h2>Live TV</h2>
        <span className="ltv-live">LIVE</span>
        {data ? (
          <span className="ltv-status">
            {data.total} channels{guideWeak ? ' · guide is updating' : ''}
          </span>
        ) : null}
        {/* tabIndex -1 on the top strip: opening Live TV must land on a channel, not here. Up from the top row reaches it. */}
        <span className="ltv-catwrap">
        <button type="button" className="ltv-catbtn" tabIndex={-1} data-ltv-top aria-haspopup="listbox" aria-expanded={catOpen ? 'true' : 'false'} onClick={() => setCatOpen((o) => !o)}>
          {cats.find((c) => c.id === cat)?.label || 'All channels'}
        </button>
        {catOpen ? (
          <div className="ltv-catmenu" data-ltv-col="cats" role="listbox" aria-label="Category">
            {cats.map((c) => (
              <button key={c.id} type="button" className="ltv-cat" data-ltv-item role="option" aria-selected={cat === c.id} aria-current={cat === c.id ? 'true' : undefined} onClick={() => pickCat(c.id)}>
                <span>{c.label}</span>
                <span>{c.count}</span>
              </button>
            ))}
          </div>
        ) : null}
        </span>
        <div className="ltv-toggle" role="group" aria-label="View">
          <button type="button" tabIndex={-1} data-ltv-top aria-pressed={view === 'list' ? 'true' : 'false'} onClick={() => switchView('list')}>
            Channels
          </button>
          <button type="button" tabIndex={-1} data-ltv-top aria-pressed={view === 'guide' ? 'true' : 'false'} onClick={() => switchView('guide')}>
            Guide
          </button>
        </div>
        {view === 'guide' ? (
          <div className="ltv-toggle">
            <button type="button" tabIndex={-1} data-ltv-top aria-label={`Guide day: ${winDay}. Press for the next day.`} onClick={nextDay}>
              {winDay} ›
            </button>
          </div>
        ) : null}
        <div className="ltv-search">
          <input tabIndex={-1} data-ltv-top placeholder="Search channels, shows, games" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search channels, shows and games" />
        </div>
      </div>
      {loadErr ? <div className="ltv-err">{loadErr}</div> : null}
      {playErr && view === 'guide' ? <div className="ltv-err" role="alert">{playErr}</div> : null}
      <div className={`ltv-cols${view === 'guide' ? ' is-guide' : ''}`}>
        {view === 'guide' ? (
          <div className="ltg" data-ltv-col="grid">
            <div className="ltg-top">
              {/* Small preview: the app's player docks over this box while a channel plays (only shown then, to save rows). */}
              {nowPlaying ? <div className="ltg-slot" data-zflix-livetv-preview-slot={playerMini ? 'true' : undefined} /> : null}
              <GuideInfo api={infoApi} watching={nowPlaying ? nowPlaying.title || nowPlaying.name || 'Live TV' : ''} />
            </div>
            <div className="ltg-scroll" ref={gRef} onScroll={gWindowed ? computeG : undefined}>
              <div className="ltg-head">
                <div className="ltg-head-chan" />
                <div className="ltg-head-times">
                  {Array.from({ length: GRID_HOURS * 2 }, (_, i) => (
                    <span key={i} style={{ left: `${(i / (GRID_HOURS * 2)) * 100}%` }}>
                      {fmtTime(winStart + i * HALF)}
                    </span>
                  ))}
                </div>
              </div>
              {data && shown.length === 0 ? <div className="ltv-empty">No channels match.</div> : null}
              {gStart > 0 ? <div aria-hidden="true" style={{ height: gStart * gStep.current }} /> : null}
              {shown.slice(gStart, gEnd).map((ch) => (
                <GuideRow
                  key={ch.key}
                  ch={ch}
                  progs={(gridLoaded ? grid.rows[ch.key] : null) || NO_PROGS}
                  loaded={gridLoaded}
                  winStart={winStart}
                  winEnd={winEnd}
                  minute={Math.floor(now / 60000)}
                  playing={playingKey === ch.key}
                  starting={startingKey === ch.key}
                  onProgFocus={onProgFocus}
                  onTune={tuneStable}
                  onFuture={futureStable}
                  recIds={recIds}
                />
              ))}
              {gEnd < shown.length ? <div aria-hidden="true" style={{ height: (shown.length - gEnd) * gStep.current }} /> : null}
            </div>
          </div>
        ) : null}

        {view === 'list' ? (
        <>
        <div className="ltv-col" data-ltv-col="channels" ref={listRef} onScroll={windowed ? computeWin : undefined}>
          {!data && !loadErr ? <div className="ltv-empty">Loading channels…</div> : null}
          {searching ? <FoundRows found={found} now={now} recIds={recIds} onPick={pickFound} onLater={noteLater} /> : null}
          {searching && shown.length ? <div className="ltv-found-head">Channels</div> : null}
          {data && shown.length === 0 && !searching ? <div className="ltv-empty">No channels match.</div> : null}
          {winStart2 > 0 ? <div aria-hidden="true" style={{ height: winStart2 * rowStep.current }} /> : null}
          {shown.slice(winStart2, winEnd2).map((ch) => (
            <ChannelRow
              key={ch.key}
              ch={ch}
              selected={sel?.key === ch.key}
              playing={playingKey === ch.key}
              starting={startingKey === ch.key}
              minute={Math.floor(now / 60000)}
              onFocusKey={setSelKey}
              onActivate={activateStable}
            />
          ))}
          {windowed && winEnd2 < shown.length ? <div aria-hidden="true" style={{ height: (shown.length - winEnd2) * rowStep.current }} /> : null}        </div>

        <div className="ltv-col ltv-pane" data-ltv-col="pane">
          {/* The app's player is positioned over this box while a channel plays (same video element when it goes full screen). */}
          <div className="ltv-slot" data-zflix-livetv-preview-slot={nowPlaying && playerMini ? 'true' : undefined}>
            {!nowPlaying ? (
              <div className="ltv-slot-idle">
                {sel?.logo ? <img src={sel.logo} alt="" /> : null}
                {sel ? 'Press OK on a channel to watch it here' : 'Pick a channel'}
              </div>
            ) : null}
          </div>
          {playErr ? <div className="ltv-err" role="alert" style={{ marginTop: 8 }}>{playErr}</div> : null}
          {sel ? (
            <>
              <div className="ltv-pane-title">{sel.name}</div>
              <div className="ltv-pane-sub">
                {sel.now ? `${sel.now.title} · until ${fmtTime(sel.now.end)}` : 'No guide information for this channel'}
              </div>
              <div className="ltv-actions">
                {selIsPlaying ? (
                  <>
                    <button type="button" className="ltv-btn ltv-btn-primary" data-ltv-item onClick={() => onExpand?.()}>
                      Full screen
                    </button>
                    <button type="button" className="ltv-btn" data-ltv-item onClick={() => onStop?.()}>
                      Stop
                    </button>
                  </>
                ) : (
                  <button type="button" className="ltv-btn ltv-btn-primary" data-ltv-item onClick={() => start(sel)} disabled={!!startingKey}>
                    {startingKey === sel.key ? 'Starting…' : 'Watch'}
                  </button>
                )}
                <button type="button" className="ltv-btn" data-ltv-item onClick={() => toggleFav(sel)}>
                  {sel.favorite ? '★ Remove favorite' : '☆ Add to favorites'}
                </button>
              </div>
              <div className="ltv-sched">
                {schedule.length === 0 ? <div className="ltv-empty">No schedule available.</div> : null}
                {schedule.map((p) => {
                  const isNow = new Date(p.start).getTime() <= now && new Date(p.end).getTime() > now;
                  return (
                    <div key={p.id} className={`ltv-sched-row${isNow ? ' is-now' : ''}`}>
                      <time>{isNow ? 'Now' : fmtTime(p.start)}</time>
                      <div>
                        {p.title}
                        {p.episodeTitle ? ` — ${p.episodeTitle}` : ''}
                        {isNow && p.overview ? <div className="ltv-sched-ov">{p.overview}</div> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
        </>
        ) : null}
      </div>
    </div>
  );
}
